/**
 * Focus Control Card Component
 * 
 * Settings card with three focus options:
 * 1. Focus Timer (1-999 minutes)
 * 2. Feed Block (1-24 hours)
 * 3. Time Window (daily recurring)
 */

import React, { useState } from 'react';
import { useFocusControl } from '../../hooks/useFocusControl';
import { formatRemainingTime } from '../../utils/scrollFreeze';
import { ThinkIcon, TimeIcon, LimitIcon, ClockIcon, TipIcon, ICON_STYLES } from '../icons';

interface FocusControlCardProps {
  onClose: () => void;
  compact?: boolean;
}

export const FocusControlCard: React.FC<FocusControlCardProps> = ({ onClose, compact = false }) => {
  const { state, startTimer, startBlock, toggleTimeWindow, updateTimeWindow } = useFocusControl();
  
  const [timerMinutes, setTimerMinutes] = useState(state.timer.durationMinutes);
  const [blockHours, setBlockHours] = useState(state.block.durationHours);
  const [windowStart, setWindowStart] = useState(state.window.startTime);
  const [windowEnd, setWindowEnd] = useState(state.window.endTime);
  const activeRemainingSeconds = state.remainingSeconds || (
    state.activeMode === 'timer' && state.timer.endsAt
      ? Math.max(0, Math.floor((new Date(state.timer.endsAt).getTime() - Date.now()) / 1000))
      : state.activeMode === 'block' && state.block.endsAt
        ? Math.max(0, Math.floor((new Date(state.block.endsAt).getTime() - Date.now()) / 1000))
        : 0
  );

  const handleStartTimer = () => {
    startTimer(timerMinutes);
    onClose();
  };

  const handleStartBlock = () => {
    startBlock(blockHours);
    onClose();
  };

  const handleToggleWindow = () => {
    // Update times first, then toggle
    updateTimeWindow(windowStart, windowEnd);
    toggleTimeWindow(!state.window.enabled);
    if (!state.window.enabled) {
      onClose(); // Close card when activating window
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[1190]" 
        onClick={onClose}
      />
      
      {/* Card */}
      <div
        className={`focus-control-card ${compact ? 'focus-control-card--compact' : ''} relative w-96 max-w-[calc(100vw-2rem)] max-h-full overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 z-[1201]`}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ 
          background: 'linear-gradient(to right, rgba(141,168,160,0.08), rgba(165,189,181,0.06))',
          borderColor: '#ECEAE5'
        }}>
          <div className="flex items-center gap-2">
            <ThinkIcon {...ICON_STYLES.default} />
            <h3 className="font-semibold text-gray-900">Focus Control</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {/* Timer Option */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TimeIcon {...ICON_STYLES.default} />
              <h4 className="font-medium text-gray-900">Focus Timer</h4>
            </div>
            <p className="text-xs text-gray-600">Korte focus sessie voor directe concentratie</p>
            {state.activeMode === 'timer' && activeRemainingSeconds > 0 && (
              <div className="rounded-lg px-3 py-2 text-center" style={{ backgroundColor: 'rgba(141,168,160,0.12)', color: '#3C4F49' }}>
                <div className="text-2xl font-mono font-bold">{formatRemainingTime(activeRemainingSeconds)}</div>
                <div className="text-[11px] font-medium">resterend</div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="999"
                value={timerMinutes}
                onChange={(e) => setTimerMinutes(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#8DA8A0' }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={timerMinutes}
                  onChange={(e) => setTimerMinutes(Math.min(999, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-16 px-2 py-1 text-sm font-medium text-gray-700 border border-gray-300 rounded text-right"
                />
                <span className="text-sm text-gray-600">min</span>
              </div>
            </div>
            
            <button
              onClick={handleStartTimer}
              disabled={state.activeMode !== 'none'}
              className="w-full px-3 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              style={{ 
                backgroundColor: state.activeMode === 'none' ? '#8DA8A0' : undefined,
                ...(state.activeMode === 'none' && {
                  ':hover': { backgroundColor: '#7A9488' }
                })
              }}
              onMouseEnter={(e) => {
                if (state.activeMode === 'none') {
                  e.currentTarget.style.backgroundColor = '#7A9488';
                }
              }}
              onMouseLeave={(e) => {
                if (state.activeMode === 'none') {
                  e.currentTarget.style.backgroundColor = '#8DA8A0';
                }
              }}
            >
              {state.activeMode === 'timer' && activeRemainingSeconds > 0
                ? `Timer loopt - ${formatRemainingTime(activeRemainingSeconds)}`
                : state.activeMode === 'timer'
                  ? 'Timer Loopt'
                  : 'Start Timer'}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Block Option */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <LimitIcon {...ICON_STYLES.default} />
              <h4 className="font-medium text-gray-900">Feed Block</h4>
            </div>
            <p className="text-xs text-gray-600">Langere blokkade om feed volledig uit te schakelen</p>
            
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="24"
                value={blockHours}
                onChange={(e) => setBlockHours(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#C9A5A0' }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={blockHours}
                  onChange={(e) => setBlockHours(Math.min(24, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-16 px-2 py-1 text-sm font-medium text-gray-700 border border-gray-300 rounded text-right"
                />
                <span className="text-sm text-gray-600">uur</span>
              </div>
            </div>
            
            <button
              onClick={handleStartBlock}
              disabled={state.activeMode !== 'none'}
              className="w-full px-3 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              style={{ 
                backgroundColor: state.activeMode === 'none' ? '#C9A5A0' : undefined
              }}
              onMouseEnter={(e) => {
                if (state.activeMode === 'none') {
                  e.currentTarget.style.backgroundColor = '#B89590';
                }
              }}
              onMouseLeave={(e) => {
                if (state.activeMode === 'none') {
                  e.currentTarget.style.backgroundColor = '#C9A5A0';
                }
              }}
            >
              {state.activeMode === 'block' ? 'Block Actief' : 'Activeer Block'}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Time Window Option */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ClockIcon {...ICON_STYLES.default} />
              <h4 className="font-medium text-gray-900">Time Window</h4>
            </div>
            <p className="text-xs text-gray-600">Dagelijks terugkerend venster waarin feed geblokkeerd is</p>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Van</label>
                <input
                  type="time"
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Tot</label>
                <input
                  type="time"
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            <button
              onClick={handleToggleWindow}
              className="w-full px-3 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
              style={{
                backgroundColor: state.window.enabled ? '#8DA8A0' : '#E8E6E1',
                color: state.window.enabled ? '#FFFFFF' : '#3C4F49'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = state.window.enabled ? '#7A9488' : '#DDD9D0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = state.window.enabled ? '#8DA8A0' : '#E8E6E1';
              }}
            >
              {state.window.enabled && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {state.window.enabled ? 'Actief' : 'Activeer Window'}
            </button>
            
            {state.window.enabled && (
              <p className="text-xs text-center" style={{ color: '#8DA8A0' }}>
                Feed geblokkeerd: {state.window.startTime} - {state.window.endTime}
              </p>
            )}
          </div>
        </div>

        {/* Footer tip */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center flex items-center justify-center gap-2">
            <TipIcon size={14} weight="duotone" />
            <span>Focus Mode helpt je bewust te focussen op wat belangrijk is</span>
          </p>
        </div>
      </div>
    </>
  );
};
